export default class Mapbox {
	static loadDependencies() {
		const mapboxVersion = 'v3.19.0'; // previously v3.19.0

		const loadJS = function() {
			return new Promise( ( resolve, reject ) => {
				const script = document.createElement( 'script' );
				script.src = `https://api.mapbox.com/mapbox-gl-js/${mapboxVersion}/mapbox-gl.js`;

				script.onload = () => {
					resolve();

					return;
				};

				script.onerror = () => {
					reject( new Error( 'mapbox gl js failed to load' ) );

					return;
				};

				script.async = true;

				const first = document.head.getElementsByTagName( 'script' )[0];
				first.parentNode.insertBefore( script, first );
			} );
		};

		const loadCSS = function() {
			return new Promise( ( resolve, reject ) => {
				const link = document.createElement( 'link' );
				link.rel = 'stylesheet';
				link.href = `https://api.mapbox.com/mapbox-gl-js/${mapboxVersion}/mapbox-gl.css`;

				link.onload = () => {
					resolve();

					return;
				};

				link.onerror = () => {
					reject( new Error( 'mapbox gl css failed to load' ) );

					return;
				};

				const first = document.head.getElementsByTagName( 'link' )[0];
				first.parentNode.insertBefore( link, first );
			} );
		};

		return Promise.all( [
			loadJS(),
			loadCSS(),
		] );
	}

	static initializeMap( node, bounds ) {
		return new Promise( ( resolve ) => {
			const map = new window.mapboxgl.Map( {
				container: node,
				style: 'mapbox://styles/mrhenry-caw/cmmnhifnw002w01sg3mj2fnp4?optimize=true',
				cooperativeGestures: true,
				bounds: bounds,
				maxBounds: [
					[
						2,
						50.2,
					], // Southwest coordinates
					[
						6,
						51.5,
					], // Northeast coordinates
				],
			} );

			map.dragRotate.disable();
			map.touchZoomRotate.disableRotation();

			const nav = new window.mapboxgl.NavigationControl( {
				showCompass: false,
				showZoom: true,
			} );

			map.addControl( nav, 'top-left' );
			map.addControl( new window.mapboxgl.FullscreenControl(), 'top-left' );

			map.on( 'load', () => {
				return resolve( map );
			} );
		} );
	}

	static buildGeoJSON( locations ) {
		return {
			type: 'FeatureCollection',
			features: Array.from( locations, ( location ) => {
				const latitude = parseFloat( location.latitude );
				const longitude = parseFloat( location.longitude );

				if ( Number.isNaN( latitude ) || Number.isNaN( longitude ) ) {
					return false;
				}

				return {
					type: 'Feature',
					geometry: {
						type: 'Point',
						coordinates: [
							longitude,
							latitude,
						],
					},
					properties: {
						popup_html: location.popup_html,
					},
				};
			} ).filter( ( location ) => {
				return !!location;
			} ),
		};
	}

	static renderMarkers( geoJSON, map ) {
		const source = map.getSource( 'markers' );

		if ( source ) {
			source.setData( geoJSON );
		} else {
			map.addSource( 'markers', {
				type: 'geojson',
				data: geoJSON,
				cluster: true,
				clusterMaxZoom: 14, // Max zoom to cluster points on
				clusterRadius: 10, // Radius of each cluster when clustering points (defaults to 50)
			} );

			map.addLayer( {
				id: 'clusters',
				type: 'circle',
				source: 'markers',
				filter: [
					'has',
					'point_count',
				],
				paint: {
					// Use step expressions (https://docs.mapbox.com/style-spec/reference/expressions/#step)
					// with three steps to implement three types of circles:
					//   * Blue, 20px circles when point count is less than 100
					//   * Yellow, 30px circles when point count is between 100 and 750
					//   * Pink, 40px circles when point count is greater than or equal to 750
					'circle-color': '#DA3B87',
					'circle-stroke-width': 3,
					'circle-stroke-color': 'rgba(218, 59, 135, 0.30)',
					'circle-emissive-strength': 1,
					'circle-radius': 10,
				},
			} );

			map.addLayer( {
				id: 'cluster-count',
				type: 'symbol',
				source: 'markers',
				filter: [
					'has',
					'point_count',
				],
				layout: {
					'text-field': [
						'get',
						'point_count_abbreviated',
					],
					'text-font': [
						'Open Sans SemiBold',
					],
					'text-size': 8,
				},
				paint: {
					'text-color': '#ffffff',
				},
			} );

			map.addLayer( {
				id: 'unclustered-point',
				type: 'circle',
				source: 'markers',
				filter: [
					'!',
					[
						'has',
						'point_count',
					],
				],
				paint: {
					'circle-color': '#DA3B87',
					'circle-radius': 10,
					'circle-stroke-width': 3,
					'circle-stroke-color': 'rgba(218, 59, 135, 0.30)',
					'circle-emissive-strength': 1,
				},
			} );

			// When a click event occurs on a cluster,
			// getClusterExpansionZoom grabs the zoomlevel where the cluster expands
			// Then the viewport zooms in to show the expanded cluster
			// Displaying the underlying individual points and/or smaller clusters
			map.addInteraction( 'click-clusters', {
				type: 'click',
				target: {
					layerId: 'clusters',
				},
				handler: ( e ) => {
					const features = map.queryRenderedFeatures( e.point, {
						layers: [
							'clusters',
						],
					} );
					const clusterId = features[0].properties.cluster_id;
					map.getSource( 'markers' ).getClusterExpansionZoom(
						clusterId,
						( err, zoom ) => {
							if ( err ) return;

							map.easeTo( {
								center: features[0].geometry.coordinates,
								zoom: zoom,
							} );
						}
					);
				},
			} );

			// When a click event occurs on a feature in
			// the unclustered-point layer, open a popup at
			// the location of the feature, with
			// description HTML from its properties.
			map.addInteraction( 'click-unclustered-point', {
				type: 'click',
				target: {
					layerId: 'unclustered-point',
				},
				handler: ( e ) => {
					const coordinates = e.feature.geometry.coordinates.slice();

					// eslint-disable-next-line no-undef
					new mapboxgl.Popup( {
						offset: [
							15,
							20,
						],
					} )
						.setLngLat( coordinates )
						.setHTML( e.feature.properties.popup_html )
						.addTo( map );
				},
			} );

			// Change the cursor to a pointer when the mouse is over a cluster of POIs.
			map.addInteraction( 'clusters-mouseenter', {
				type: 'mouseenter',
				target: {
					layerId: 'clusters',
				},
				handler: () => {
					map.getCanvas().style.cursor = 'pointer';
				},
			} );

			// Change the cursor back to a pointer when it stops hovering over a cluster of POIs.
			map.addInteraction( 'clusters-mouseleave', {
				type: 'mouseleave',
				target: {
					layerId: 'clusters',
				},
				handler: () => {
					map.getCanvas().style.cursor = '';
				},
			} );

			// Change the cursor to a pointer when the mouse is over an individual POI.
			map.addInteraction( 'unclustered-mouseenter', {
				type: 'mouseenter',
				target: {
					layerId: 'unclustered-point',
				},
				handler: () => {
					map.getCanvas().style.cursor = 'pointer';
				},
			} );

			// Change the cursor back to a pointer when it stops hovering over an individual POI.
			map.addInteraction( 'unclustered-mouseleave', {
				type: 'mouseleave',
				target: {
					layerId: 'unclustered-point',
				},
				handler: () => {
					map.getCanvas().style.cursor = '';
				},
			} );
		}
	}
}
